import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnInit,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { IPKSettings } from '../pk';
import { PkService } from '../pk.service';
import { AppTextService } from '../../../shared/app-text';
import { FiDialogsService } from '../../../shared/fi-dialogs';
import { FiCustomerDataService } from '../../../shared/fi-shared-services/fi-customer-data.service';
import { FiSnackBarService } from '../../../shared/fi-snackbar/snack-bar.service';
import { ILightWeightArea } from '@fi-shared/models';
import { fiItemsFlatten } from '@fi-shared/utils/fi-items-flatten';
import { FiValidatingFormDirective } from '@fi-shared/fi-directives/fi-validating-form/fi-validating-form.directive';
import { TResultPromise, TResultPromiseData } from '@fi-shared/fi-shared-interfaces';
import { PKUnitsImportType } from '@fi-app/external-platform-integration/pk/pk-units-import-type';
import { PKUsersImportType } from '@fi-app/external-platform-integration/pk/pk-users-import-type';

@Component({
    selector: 'pk-settings',
    templateUrl: './pk-settings.component.html',
    styleUrls: ['./pk-settings.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'pk-settings' },
    standalone: false,
})
export class PkSettingsComponent implements OnInit {
    @Input() pkSettings: IPKSettings;
    @Input() isAutoImportAvailable: boolean;
    @Input() areas: ILightWeightArea[];

    @ViewChild(FiValidatingFormDirective) pkSettingsForm: FiValidatingFormDirective;

    isDirty: boolean;
    flattenAreas: ILightWeightArea[];

    constructor(
        private pkService: PkService,
        private cdr: ChangeDetectorRef,
        private appTextService: AppTextService,
        private fiDialogsService: FiDialogsService,
        private customerDataService: FiCustomerDataService,
        private fiSnackBarService: FiSnackBarService,
    ) {}

    ngOnInit(): void {
        this.flattenAreas = this.areas.filter((area) => area.Level === 0);
        if (!this.isAutoImportAvailable) {
            this.pkSettings.IsAutoImportEnabled = false;
        }
    }

    getSettingsAutoImportDescription(): string {
        const displayName = this.getCustomerDisplayName();
        if (!this.isAutoImportAvailable)
            return this.appTextService.get('pk.settings_description_disabled', {
                '{0}': displayName,
            });

        if (!this.pkSettings.IsAutoImportEnabled)
            return this.appTextService.get('pk.settings_description_off', {
                '{0}': displayName,
            });

        return this.appTextService.get('pk.settings_description_on', {
            '{0}': displayName,
        });
    }

    onIsEnabledChange() {
        const promise: TResultPromise = new Promise<TResultPromiseData>((resolve) => {
            if (!this.pkSettings.IsAutoImportEnabled) {
                resolve({ result: true });
                return;
            }

            this.fiDialogsService
                .confirm({
                    headerText: this.appTextService.get('pk.settings_dialog_header'),
                    messageText: this.appTextService.get('pk.settings_dialog_content', {
                        '{0}': this.getCustomerDisplayName(),
                    }),
                    yesButtonText: this.appTextService.get('common.activate'),
                })
                .onYesButtonClick(() => {
                    resolve({ result: true });
                })
                .onNoButtonClick(() => {
                    this.pkSettings.IsAutoImportEnabled = false;
                    this.cdr.markForCheck();
                    resolve({ result: false });
                });
        });

        promise.then((result) => {
            if (result.result) {
                this.pkSettings.SendEmailOnError = this.pkSettings.IsAutoImportEnabled;
                this.pkSettings.CreateFeedItemOnError = this.pkSettings.IsAutoImportEnabled;
                this.onSettingsChanged();
            }
        });
    }

    getSliderStateText() {
        return this.appTextService.get(this.pkSettings.IsAutoImportEnabled ? 'on' : 'off');
    }

    getUnitsImportTypeText() {
        const displayName = this.getCustomerDisplayName();
        return this.appTextService.get('pk.settings_units_import_type', {
            '{0}': displayName,
        });
    }

    isAutoImportSettingDisabled() {
        return !this.isAutoImportAvailable || !this.pkSettings.AreaId;
    }

    canSavePKSettings() {
        return this.isDirty && !this.pkSettingsForm.errors;
    }

    onSettingsChanged() {
        this.pkSettingsForm.updateValidity();
        this.isDirty = true;
    }

    savePKSettings() {
        this.pkService.savePKSettings(this.pkSettings).then((newPersonalkollenData) => {
            this.cdr.markForCheck();
            this.isDirty = false;
            this.customerDataService.setPersonalkollenData(newPersonalkollenData);
            //this.showSnackBar();
        });
    }

    /*private showSnackBar(): void {
        this.fiSnackBarService.showSnackbar({
            textMessage: this.appTextService.get(this.isAutoImportEnabled ? 'pk.snackbar_auto_import_enabled' :
                'pk.snackbar_auto_import_disabled'),
            buttonText: this.appTextService.get('common.dismiss')
        });
    }*/

    private getCustomerDisplayName() {
        return this.customerDataService.customerData.DisplayName;
    }

    protected readonly PKUnitsImportType = PKUnitsImportType;
    protected readonly PKUsersImportType = PKUsersImportType;
}
